import { Controller } from "@hotwired/stimulus"
import type { AlertType } from "@/types/utilities"
import { t } from "@/utils/i18n"

export default class BaseController extends Controller {
  // Text replaced by showLoadingState, remembered per element so two
  // concurrent loading states cannot restore each other's label.
  private originalTextByElement = new WeakMap<HTMLElement, string>()
  
  connect(): void {
    // Controller connected - base functionality ready
  }

  disconnect(): void {
    // Controller disconnected - cleanup complete
  }

  // === ERROR HANDLING ===
  
  protected handleError(error: Error | unknown, context: string = ""): void {
    console.error(`[${this.identifier}] Error in ${context}:`, error)
    this.showErrorMessage(t('generic_error'))
  }

  protected handleSuccess(message: string): void {
    this.showSuccessMessage(message)
  }

  // === DEBUGGING ===
  
  protected debug(message: string, data: unknown = null): void {
    if (this.isDevelopment()) {
      console.log(`[${this.identifier}] ${message}`, data)
    }
  }

  protected isDevelopment(): boolean {
    return import.meta.env.MODE === 'development'
  }

  // === UI HELPERS ===
  
  protected showErrorMessage(message: string): void {
    this.showAlert(message, 'error')
  }

  protected showSuccessMessage(message: string): void {
    this.showAlert(message, 'success')
  }

  protected showInfoMessage(message: string): void {
    this.showAlert(message, 'info')
  }

  protected showWarningMessage(message: string): void {
    this.showAlert(message, 'warning')
  }

  protected showAlert(message: string, type: AlertType = 'info'): void {
    const alert = document.createElement('div')
    alert.className = `alert alert-${type} fixed top-4 right-4 z-50 w-96 shadow-lg`
    
    const icons: Record<AlertType, string> = {
      success: `<svg class="w-6 h-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>`,
      error: `<svg class="w-6 h-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>`,
      warning: `<svg class="w-6 h-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>`,
      info: `<svg class="w-6 h-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
             </svg>`
    }
    
    alert.innerHTML = `
      ${icons[type]}
      <div>
        <span>${message}</span>
        <button class="btn btn-sm btn-ghost ml-2" onclick="this.parentElement.parentElement.remove()">✕</button>
      </div>
    `
    
    document.body.appendChild(alert)
    
    setTimeout(() => {
      if (alert.parentElement) {
        alert.remove()
      }
    }, 5000)
  }

  // === LOADING STATE ===
  
  protected showLoadingState(element: HTMLElement | null, text: string = t('loading')): void {
    if (!element) return
    
    element.classList.add('loading')
    element.setAttribute('disabled', 'true')
    this.originalTextByElement.set(element, element.textContent ?? '')
    if (text !== element.textContent) {
      element.textContent = text
    }
  }

  protected hideLoadingState(element: HTMLElement | null): void {
    if (!element) return
    
    element.classList.remove('loading')
    element.removeAttribute('disabled')
    const original = this.originalTextByElement.get(element)
    if (original !== undefined) {
      element.textContent = original
      this.originalTextByElement.delete(element)
    }
  }

  // === CSRF & FORMS ===
  
  protected getCsrfToken(): string | null {
    const token = document.querySelector<HTMLMetaElement>('[name="csrf-token"]')
    return token ? token.getAttribute('content') : null
  }

  protected getFormData(form: HTMLFormElement | string): FormData {
    let formElement: HTMLFormElement | null
    
    if (typeof form === 'string') {
      formElement = document.querySelector<HTMLFormElement>(form)
    } else {
      formElement = form
    }
    
    if (!formElement) {
      throw new Error('Form not found')
    }
    
    return new FormData(formElement)
  }

  // === UTILITY FUNCTIONS ===
  
  protected debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>
    return (...args: Parameters<T>) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  protected throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  }

  // === TURBO HELPERS ===
  
  protected createTurboForm(
    action: string,
    method: string = 'POST',
    data: Record<string, string> = {},
    frame?: string
  ): HTMLFormElement {
    const form = document.createElement('form')
    form.method = method === 'GET' ? 'GET' : 'POST'
    form.action = action
    // Only target a Turbo frame when the caller asks for one. This used to be
    // hardcoded to "entry_form", so unrelated flows (deletes, logout) aimed
    // their responses at the entry modal's frame.
    if (frame) {
      form.setAttribute('data-turbo-frame', frame)
    }

    const csrfToken = this.getCsrfToken()
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }
    
    if (method !== 'GET' && method !== 'POST') {
      const methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      methodInput.value = method
      form.appendChild(methodInput)
    }
    
    Object.keys(data).forEach(key => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = data[key]
      form.appendChild(input)
    })
    
    return form
  }

  protected submitTurboForm(form: HTMLFormElement): void {
    const url = form.action
    const method = form.method.toUpperCase()
    const frame = form.getAttribute('data-turbo-frame') || undefined

    if (method === 'GET') {
      const formData = new FormData(form)
      const params = new URLSearchParams(formData as any).toString()
      const fullUrl = params ? `${url}?${params}` : url

      Turbo.visit(fullUrl, frame ? { frame } : {})
    } else {
      form.style.display = 'none'
      document.body.appendChild(form)
      
      form.requestSubmit()
      
      const cleanup = () => {
        if (form.parentNode) {
          document.body.removeChild(form)
        }
      }
      
      form.addEventListener('turbo:submit-end', cleanup, { once: true })
      setTimeout(cleanup, 1000)
    }
  }
}

